// FilesTab (#1478, updated #2102) — READ-ONLY request-attachment list for
//   the post-submission drawer on ALL China request kinds (SIMPLE,
//   STANDARD, CONTAINER). Lists request-level files with a download button.
//   Unlike `RequestFilesField` there is NO upload, NO remove, and NO inline
//   image thumbnail (icon only) — viewers must not mutate attachments from
//   this surface.

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { RequestFile } from '@magimp/shared';
import { Button, EmptyState, ErrorState, Icon, LoadingState } from '../../../../ui/index.js';
import { formatDateTime, formatSize } from '../../../../i18n/index.js';
import { listRequestFiles, requestFileDownloadUrl } from '../../../../api/requests/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { requestsKeys } from '../../queries.js';
import type { RequestView } from '../../types.js';

export function FilesTab({ request }: { request: RequestView }) {
  const { t } = useTranslation(['requests', 'common']);
  const showToast = useToastStore((s) => s.show);
  const filesQuery = useQuery({
    queryKey: requestsKeys.files(request.id),
    queryFn: () => listRequestFiles(request.id),
  });

  async function onDownload(file: RequestFile) {
    try {
      const res = await requestFileDownloadUrl(request.id, file.id);
      // INVARIANT (#1478): on failure show the toast and never call
      //   `window.open`; never log the file name / key / presigned url.
      window.open(res.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      showToast({ tone: 'error', text: t('requests:files.errors.downloadFailed') });
    }
  }

  if (filesQuery.isLoading) return <LoadingState />;
  if (filesQuery.isError) {
    return (
      <ErrorState
        text={t('requests:files.errors.listFailed')}
        onRetry={() => void filesQuery.refetch()}
      />
    );
  }

  const files = filesQuery.data ?? [];
  if (files.length === 0) {
    return (
      <div data-testid="request-files-tab-empty">
        <EmptyState icon="paperclip" title={t('requests:files.empty')} />
      </div>
    );
  }

  return (
    <ul
      data-testid="request-files-tab-list"
      style={{
        margin: 0,
        padding: 12,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {files.map((file) => (
        <FileRow key={file.id} file={file} onDownload={() => void onDownload(file)} />
      ))}
    </ul>
  );
}

function FileRow({ file, onDownload }: { file: RequestFile; onDownload: () => void }) {
  const { t } = useTranslation(['requests', 'common']);
  return (
    <li
      data-testid="request-files-tab-row"
      data-file-id={file.id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
      }}
    >
      <Icon name={file.contentType.startsWith('image/') ? 'image' : 'file-text'} size={16} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          title={file.fileName}
          style={{
            fontSize: 13,
            color: 'var(--fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.fileName}
        </span>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {formatSize(file.contentLength)} · {formatDateTime(file.createdAt)}
        </span>
      </div>
      <Button
        kind="ghost"
        size="sm"
        icon="download"
        aria-label={t('requests:files.download')}
        onClick={onDownload}
        data-testid="request-files-tab-download"
      />
    </li>
  );
}
